import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import type { ComponentProps } from "react";

import {
  getPageTitle,
  getPageTitleHref,
  shouldShowDefaultPageTitle,
} from "@src/features/app/top-nav/pageTitle";
import BuilderHeaderBackButton from "@src/features/app/top-nav/BuilderHeaderBackButton";
import { useDefaultPageTitleHidden } from "@src/features/app/top-nav/useDefaultPageTitleHidden";
import {
  getBuilderBackHref,
  getBuilderDomain,
  getStorageKey,
  isBuilderView,
  isTrackableListView,
  getBuilderInternalBackHref,
} from "@src/features/app/top-nav/builderNav";
import { cn } from "@src/lib/utils/cn";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";

type LinkHref = ComponentProps<typeof Link>["href"];

const PAGE_TITLE_CLASS = "truncate text-xl font-semibold tracking-[-0.32px]";
const BREADCRUMB_LINK_CLASS =
  "truncate rounded text-sm font-medium tracking-[-0.006em] text-muted-foreground transition-colors hover:text-foreground";

const PageContentHeaderZone: React.FC = () => {
  const router = useRouter();
  const [hasPageTitleOverride, setHasPageTitleOverride] = React.useState(false);
  const [rememberedBuilderBackHref, setRememberedBuilderBackHref] =
    React.useState<LinkHref | null>(null);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const operationsBundle = !auditsEnabled && !taskLibraryItemTypeEnabled;
  const isBuilder = isBuilderView(router.pathname, router.query);
  const pageTitle = getPageTitle(
    router.pathname,
    router.query,
    operationsBundle,
  );
  const pageTitleHref = getPageTitleHref(router.pathname, router.query);
  const builderBackFallbackHref = isBuilder
    ? getBuilderBackHref(router.pathname, router.query)
    : null;
  const builderInternalBackHref = isBuilder
    ? getBuilderInternalBackHref(router.pathname, router.query)
    : null;
  const builderBackHref =
    builderInternalBackHref ||
    rememberedBuilderBackHref ||
    builderBackFallbackHref;
  const titleHiddenByPage = useDefaultPageTitleHidden();
  const showDefaultTitle =
    !isBuilder &&
    !hasPageTitleOverride &&
    !titleHiddenByPage &&
    shouldShowDefaultPageTitle(router.pathname);

  React.useEffect(() => {
    const target = document.getElementById("top-nav-page-title-override");
    if (!target) return;

    const syncOverrideState = () => {
      setHasPageTitleOverride(target.childNodes.length > 0);
    };
    syncOverrideState();

    const observer = new MutationObserver(syncOverrideState);
    observer.observe(target, { childList: true });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!router.isReady) return;

    const domain = getBuilderDomain(router.pathname);

    if (isTrackableListView(router.pathname, router.query) && domain) {
      window.sessionStorage.setItem(getStorageKey(domain), router.asPath);
      setRememberedBuilderBackHref(null);
      return;
    }

    if (isBuilder && domain) {
      const storedBackHref = window.sessionStorage.getItem(
        getStorageKey(domain),
      );
      setRememberedBuilderBackHref(
        storedBackHref ? (storedBackHref as LinkHref) : null,
      );
    }
  }, [isBuilder, router.asPath, router.isReady, router.pathname, router.query]);

  if (router.pathname === "/" && !isBuilder && !builderBackHref) {
    return null;
  }

  if (router.pathname.startsWith("/library/folders")) {
    return null;
  }

  if (router.pathname.startsWith("/settings")) {
    return null;
  }

  if (router.pathname.startsWith("/guest-feedback/")) {
    return null;
  }

  if (router.pathname.startsWith("/messages")) {
    return null;
  }

  return (
    <div
      className={cn(
        "shrink-0 px-2 md:px-4",
        isBuilder ? "border-b border-gray-2 py-2" : "py-3",
      )}
    >
      <div className="relative flex min-w-0 items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <div
              id="top-nav-page-title-leading"
              className="flex items-center gap-1.5 empty:hidden"
            >
              {isBuilder && builderBackHref ? (
                <BuilderHeaderBackButton
                  href={builderBackHref}
                  replace={builderInternalBackHref != null}
                />
              ) : null}
            </div>
            {!isBuilder && (
              <div
                id="top-nav-page-title-override"
                className="flex min-w-0 items-center empty:hidden"
              />
            )}
            {showDefaultTitle &&
              (pageTitleHref ? (
                <Link href={pageTitleHref} className={BREADCRUMB_LINK_CLASS}>
                  {pageTitle}
                </Link>
              ) : (
                <h1 className={PAGE_TITLE_CLASS}>{pageTitle}</h1>
              ))}
            <div
              id="top-nav-page-title-extra"
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                isBuilder ? "items-start" : "items-center",
              )}
            />
          </div>
          <div id="top-nav-page-subtitle" className="min-w-0 empty:hidden" />
        </div>
        <div
          id="top-nav-main-tabs"
          className="absolute left-1/2 flex -translate-x-1/2 items-center empty:hidden"
        />
        <div
          id="top-nav-actions"
          className="flex shrink-0 items-center gap-1.5 empty:hidden"
        />
      </div>
    </div>
  );
};

export default PageContentHeaderZone;
